import { useEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import { useTranslation } from '@klaviyo-mkt/gatsby-components/hooks/useTranslation';
import Prism from 'prismjs';

import ArticleMeta from '@klaviyo-mkt/gatsby-components/molecules/article-meta';
import ArticleVoteForm from '@klaviyo-mkt/gatsby-components/molecules/article-vote-form';
import TwoColumnLayout from '@klaviyo-mkt/gatsby-components/utils/two-column-layout';
import GlobalHead from '../../components/head';
import Layout from '../../layout';
import parser from './parser';

import * as s from './styles';
import './styles/prism.css';

import type { MouseEventHandler } from 'react';
import type { HeadProps, PageProps } from 'gatsby';
import type { IPageContext } from '../../components/head/types';
import type { IHCLayout } from '../../layout/types';

Prism.manual = true;

export const Article = ({ location, data: { article }, ...pageProps }: PageProps<Queries.ArticleQuery>) => {
  const { hash } = location;
  const { t } = useTranslation();

  const articleContentRef = useRef<HTMLDivElement>(null);
  const [hasClientRendered, setHasClientRendered] = useState(false);
  const [modalState, setModalState] = useState(false);
  const [imageElementSrc, setImageElementSrc] = useState('');
  const [imageElementAlt, setImageElementAlt] = useState('');
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  const handleClick: MouseEventHandler<HTMLImageElement> = (e) => {
    const imageElement = e.target as HTMLImageElement;
    setImageElementSrc(imageElement.src);
    setImageElementAlt(imageElement.alt);
    setModalState(true);
  };

  useEffect(() => {
    setHasClientRendered(true);
  }, []);

  useEffect(() => {
    const content = articleContentRef.current;
    if (!hasClientRendered || !content) return;
  
    const images = content.querySelectorAll('img');
    let loadedCount = 0;

    const resolve = () => {
      loadedCount++;
      if (loadedCount === images.length) {
        setAllImagesLoaded(true);
      }
    };
  
    images.forEach((img) => {
      if (img.complete) {
        loadedCount++;
      } else {
        img.onload = resolve;
        img.onerror = resolve;
      }
    });
  
    if (loadedCount === images.length) {
      setAllImagesLoaded(true);
    }
  
    return () => {
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [hasClientRendered]);

  useEffect(() => {
    if (hasClientRendered && allImagesLoaded) {
      Prism.highlightAll();

      const hashTarget = document.getElementById(hash.replace('#', ''));
      if (hashTarget) {
        const offset = hashTarget.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: offset - 100, behavior: 'smooth' });
      }
    }
  }, [hasClientRendered, allImagesLoaded, hash]);

  if (article === null || article.draft) {
    return null;
  }
  
  const { sourceId, rawHtmlBody, section, title, updatedAt } = article;

  const { h2List, parsedBody, readTime } = parser(rawHtmlBody || '', handleClick);

  return (
    <Layout
    {...pageProps}
      headerChildren={(
        <s.ArticleHeader>
          <s.Breadcrumbs>
            <s.Breadcrumb to={`/hc/${article.locale}`}>{t('helpCenter_siteName')}</s.Breadcrumb>
            <s.Slash>/</s.Slash>
            <s.Breadcrumb to={`/hc/${article.locale}/categories/${section?.category?.sourceId}`}>{section?.category?.name}</s.Breadcrumb>
            <s.Slash>/</s.Slash>
            {section?.parentSection?.id !== undefined && (
              <>
                <s.Breadcrumb to={`/hc/${article.locale}/sections/${section?.parentSection?.sourceId}`}>{section?.parentSection?.name}</s.Breadcrumb>
                <s.Slash>/</s.Slash>
              </>
            )}
            <s.Breadcrumb to={`/hc/${article.locale}/sections/${section?.sourceId}`}>{section?.name}</s.Breadcrumb>
          </s.Breadcrumbs>
          <ArticleMeta
            date={updatedAt || undefined}
            readTime={readTime}
            title={title || ''}
            topic={section?.category?.name || ''}
            topicSourceId={section?.category?.sourceId || ''}
          />
        </s.ArticleHeader>
      )}
      location={location}
      translations={article?.translations?.filter(Boolean) as IHCLayout['translations']}
    >
      <s.ArticlePage>
        <TwoColumnLayout
          layout="20-80"
          headline={null}
          childrenLeft={(
            <s.StyledArticleTableOfContents
              sections={h2List}
            />
          )}
          childrenRight={(
            <>
              <s.Content>
                <s.ArticleContent ref={articleContentRef}>
                  <style>
                    {`
                      .bs-callout::before { content: '${t('helpCenter_article_callout_general', { defaultValue: 'Note' })}'; }
                      .bs-callout-warning::before { content: '${t('helpCenter_article_callout_warning', { defaultValue: 'Warning' })}'; }
                      .bs-callout-requirements::before { content: '${t('helpCenter_article_callout_requirements', { defaultValue: 'Requirements' })}'; }
                    `}
                  </style>
                  {parsedBody}
                </s.ArticleContent>
                <s.StyledKlaviyoModal
                  size="constrained"
                  backgroundColor="lightSand"
                  isMediaModal={true}
                  show={modalState}
                  close={() => setModalState(false)}
                >
                  <s.ImageContainer>
                    <s.Image
                      src={imageElementSrc}
                      alt={imageElementAlt}
                    />
                  </s.ImageContainer>
                </s.StyledKlaviyoModal>
              </s.Content>
              {sourceId && (
                <ArticleVoteForm
                  articleId={sourceId}
                  category={section?.category?.name ?? ''}
                  locale={article?.locale ? article.locale : 'en-us'}
                />
              )}
            </>
          )}
        />
      </s.ArticlePage>
    </Layout>
  );
};

export default Article;

export const Head = ({ data: { article }, location, pageContext }: HeadProps<Queries.ArticleQuery, IPageContext>) => (
  <GlobalHead
    langCode={pageContext?.locale}
    pathname={location.pathname}
    name={article?.name || ''}
    pageType="article"
    seo={{
      canonical: `https://help.klaviyo.com/hc/${article?.locale}/articles/${article?.sourceId}`,
    }}
    translations={article?.translations?.filter(Boolean) as IHCLayout['translations']}
  >
    <script src='https://fast.wistia.com/assets/external/E-v1.js' async={true} />
  </GlobalHead>
);

export const query = graphql`
  query Article($id: String) {
    article(id: { eq: $id }) {
      archived
      body
      createdAt
      draft
      htmlBody
      htmlUrl
      id
      includesVideoEmbed
      locale
      name
      promoted
      rawHtmlBody
      section {
        category {
          id
          name
          slug
          sourceId
        }
        id
        name
        parentSection {
          id
          name
          slug
          sourceId
        }
        slug
        sourceId
      }
      slug
      sourceId
      title
      translations {
        hreflang
        locale
        name
        url
      }
      updatedAt
    }
  }
`;
